import * as React from 'react';
import { FormEvent, FunctionComponent, useContext, useEffect } from 'react';

import { FormHelperText, ThemeProvider } from '@mui/material';
import { keys } from 'lodash';

import { ActiveInactiveStatusEnum } from '../../../../shared/enums/active-inactive-status.enum';
import { useSyncFileGlobalStateValueWithFormik } from '../../../../shared/hooks/use-sync-file-global-state-value-with-formik/use-sync-file-global-state-value-with-formik.hook';
import { convertToSentenceCase } from '../../../../shared/utils/convert-to-sentence-case/convert-to-sentence-case.util';
import { mapActiveInactiveStatusEnumToItemLabelModel } from '../../../../shared/utils/mapping/mapping.util';
import { ChangeDetectorContext } from '../../../shared/components/change-detector-context/ChangeDetectorContext';
import { ChangeDetectorContextModel } from '../../../shared/components/change-detector-context/models/change-detector-context.model';
import DragAndDrop from '../../../shared/components/drag-and-drop/DragAndDrop';
import Dropdown from '../../../shared/components/dropdown/Dropdown';
import Modal from '../../../shared/components/modal/Modal';
import { ModalContext } from '../../../shared/components/modal/ModalContext';
import { ModalContextModel } from '../../../shared/components/modal/models/modal-context.model';
import Autocomplete from '../../../shared/components/ui/Autocomplete';
import Box from '../../../shared/components/ui/Box';
import Button from '../../../shared/components/ui/Button';
import CharacterCounter from '../../../shared/components/ui/CharacterCounter/CharacterCounter';
import FormControl from '../../../shared/components/ui/FormControl';
import Input from '../../../shared/components/ui/Input';
import { FileMetadataTypesEnum } from '../../../shared/enums/file-metadata-types.enum';
import { formTheme } from '../../../shared/themes/form-theme/form.theme';
import { castTo } from '../../../shared/utils/cast-to';
import { getRenderInput } from '../../../shared/utils/get-render-input/get-render-input.util';
import {
  mapFileMetadataModelToIdFile,
  mapPlatformDtoToItemLabelModel,
} from '../../../shared/utils/mapping/mapping.utils';
import { optionEquality } from '../../../shared/utils/option-equality/option-equality.util';
import { useGetBrands } from '../../hooks/use-get-brands/use-get-brands.hook';
import { useGetEcusForModel } from '../../hooks/use-get-ecus-for-model/use-get-ecus-for-model.hook';
import { useGetModelsForBrand } from '../../hooks/use-get-models-for-brand/use-get-models-for-brand.hook';
import {
  removeFirmwareFile,
  setFirmwareFile,
} from '../../store/slices/firmware.slice';
import {
  mapAttendedECUToItemLabelModel,
  mapBrandModelToItemLabelModel,
  mapModelEntityToItemLabelModel,
} from '../../utils/mapping/mapping.util';
import { setInputValue } from '../../utils/set-input-value/set-input-value.util';
import { useOnFirmwareDetailsDependentSelectChange } from './hooks/use-on-firmware-details-dependent-select-change/use-on-firmware-details-dependent-select-change.hook';
import { FirmwareDetailsFormModel } from './models/firmware-form.model';
import { OuterFirmwareFormPropsModel } from './models/outer-firmware-form-props.model';
import { WithFormik } from './types/with-formik.type';

const FirmwareDetailsForm: FunctionComponent<
  WithFormik<OuterFirmwareFormPropsModel, FirmwareDetailsFormModel>
> = (props): JSX.Element => {
  const {
    values,
    handleBlur,
    handleChange,
    errors,
    touched,
    dirty,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    cancelAction,
    firmwareFile,
    firmwareFileId,
  } = props;

  const ctx = useContext(ModalContext) as ModalContextModel;
  const cdContext = useContext(
    ChangeDetectorContext
  ) as ChangeDetectorContextModel;

  const brands = useGetBrands();
  const models = useGetModelsForBrand(values.brand?.id);
  const ecus = useGetEcusForModel(values.model?.id);

  const brandRenderInput = getRenderInput<FirmwareDetailsFormModel>(
    'Brand/Vehicle',
    'brand',
    errors,
    touched,
    true,
    true,
    handleBlur
  );

  const modelRenderInput = getRenderInput<FirmwareDetailsFormModel>(
    'Model',
    'model',
    errors,
    touched,
    true,
    true,
    handleBlur
  );

  const ecusRenderInput = getRenderInput<FirmwareDetailsFormModel>(
    'Attended ECU',
    'ecu',
    errors,
    touched,
    true,
    true,
    handleBlur
  );

  const statusRenderInput = getRenderInput<FirmwareDetailsFormModel>(
    'Status',
    'status',
    errors,
    touched,
    true
  );

  const setPlatformsValue = setInputValue<FirmwareDetailsFormModel>(
    setFieldValue,
    'platform'
  );

  const setBrandValue = useOnFirmwareDetailsDependentSelectChange(
    setFieldValue,
    'brand',
    [
      { field: 'model', valueToReset: null },
      { field: 'ecu', valueToReset: null },
    ],
    setFieldTouched
  );

  const setModelValue = useOnFirmwareDetailsDependentSelectChange(
    setFieldValue,
    'model',
    [{ field: 'ecu', valueToReset: null }],
    setFieldTouched
  );

  const setECUValue = setInputValue<FirmwareDetailsFormModel>(
    setFieldValue,
    'ecu',
    setFieldTouched
  );

  const setStatusValue = setInputValue<FirmwareDetailsFormModel>(
    setFieldValue,
    'status'
  );

  const platformsRenderInput = getRenderInput<FirmwareDetailsFormModel>(
    'Platform',
    'platform',
    errors,
    touched,
    true,
    true
  );

  const optionsStatus = [
    ActiveInactiveStatusEnum.ACTIVE,
    ActiveInactiveStatusEnum.INACTIVE,
  ];

  const isFirmwareFileModified = firmwareFile?.id !== values.firmwareFileId;

  useEffect(() => {
    cdContext.setIsChanged(
      (dirty && keys(touched).length > 0) || isFirmwareFileModified
    );
  }, [dirty, touched, isFirmwareFileModified]);

  useSyncFileGlobalStateValueWithFormik<FirmwareDetailsFormModel>(
    setFieldValue,
    firmwareFileId,
    'firmwareFileId',
    values.firmwareFileId
  );

  return (
    <>
      <ThemeProvider theme={formTheme}>
        <Box
          sx={{
            my: '48px',
          }}
          data-testid="create-edit-firmware"
        >
          <Box
            component={'form'}
            onSubmit={event =>
              handleSubmit(castTo<FormEvent<HTMLFormElement>>(event))
            }
            sx={{
              maxWidth: '500px',
              margin: 'auto',
              width: '100%',
              px: '6px',
              boxSizing: 'border-box',
            }}
          >
            <FormControl>
              <Input
                id="name"
                label="Firmware name"
                variant="filled"
                name="name"
                type="text"
                fullWidth={true}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!errors.name && touched.name}
                helperText={!!errors.name && touched.name && errors.name}
                value={values.name}
                inputProps={{
                  maxLength: '30',
                }}
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>
            <FormControl>
              <Dropdown
                endpoint="/platforms"
                dataMap={mapPlatformDtoToItemLabelModel}
                autoComplete={{
                  className: 'dropdown-autocomplete',
                  sx: {
                    width: '100%',
                  },
                  value: values.platform,
                  onChange: setPlatformsValue,
                  isOptionEqualToValue: optionEquality,
                  multiple: false,
                  filterSelectedOptions: true,
                  renderInput: platformsRenderInput,
                  onBlur: handleBlur,
                  id: 'platform',
                }}
              />
            </FormControl>
            <FormControl>
              <Autocomplete
                className={'dropdown-autocomplete'}
                sx={{
                  width: '100%',
                  height: '56px',
                }}
                id="brand"
                value={values.brand}
                isOptionEqualToValue={optionEquality}
                onChange={setBrandValue}
                renderInput={brandRenderInput}
                options={brands.map(mapBrandModelToItemLabelModel)}
              />
            </FormControl>
            <FormControl>
              <Autocomplete
                className={'dropdown-autocomplete'}
                sx={{
                  width: '100%',
                  height: '56px',
                }}
                id="model"
                value={values.model}
                isOptionEqualToValue={optionEquality}
                onChange={setModelValue}
                renderInput={modelRenderInput}
                options={
                  values.brand ? models.map(mapModelEntityToItemLabelModel) : []
                }
              />
            </FormControl>
            <FormControl>
              <Autocomplete
                className={'dropdown-autocomplete'}
                sx={{
                  width: '100%',
                  height: '56px',
                }}
                id="attendedECU"
                onChange={setECUValue}
                value={values.ecu}
                isOptionEqualToValue={optionEquality}
                renderInput={ecusRenderInput}
                options={
                  values.model ? ecus.map(mapAttendedECUToItemLabelModel) : []
                }
              />
            </FormControl>
            <FormControl>
              <Input
                sx={{
                  width: '488px',
                }}
                label="Release description"
                variant="filled"
                id="description"
                name="description"
                type="text"
                multiline={true}
                fullWidth={true}
                rows={9}
                value={values.description}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!errors.description && touched.description}
                helperText={
                  !!errors.description &&
                  touched.description &&
                  errors.description
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                inputProps={{
                  maxLength: '4000',
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />

              <CharacterCounter
                className={'input-helper-text'}
                current={values?.description?.length ?? 0}
                max={4000}
              />
            </FormControl>
            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                id="versionName"
                label="Version Name"
                variant="filled"
                name="versionName"
                type="text"
                fullWidth={true}
                value={values.versionName}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!errors.versionName && touched.versionName}
                helperText={
                  !!errors.versionName &&
                  touched.versionName &&
                  errors.versionName
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                    disabled: 'input-disabled',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>
            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                id="versionCode"
                label="Version Code"
                variant="filled"
                name="versionCode"
                type="text"
                fullWidth={true}
                value={values.versionCode ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!errors.versionCode && touched.versionCode}
                helperText={
                  !!errors.versionCode &&
                  touched.versionCode &&
                  errors.versionCode
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                    disabled: 'input-disabled',
                  },
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>
            <FormControl sx={{ flexWrap: 'wrap' }}>
              <DragAndDrop
                name="FirmwareDetailsFormDnD"
                removeHandler={removeFirmwareFile}
                uploadHandler={setFirmwareFile}
                splitIntoChunks={true}
                multiple={false}
                label="Upload File"
                isRequired={true}
                {...(firmwareFile && {
                  files: [mapFileMetadataModelToIdFile(firmwareFile)],
                })}
                hasError={!!errors.firmwareFileId && touched.firmwareFileId}
                helperText="Drag or drop ZIP file here to upload"
                buttonLabel={firmwareFile ? 'Change file' : 'Browse files'}
                fileTypeAccepted={'.zip'}
                previewType={FileMetadataTypesEnum.APK}
                fileType={FileMetadataTypesEnum.FW}
              ></DragAndDrop>

              {touched.firmwareFileId && !!errors.firmwareFileId && (
                <FormHelperText error>{errors.firmwareFileId}</FormHelperText>
              )}
            </FormControl>
            <FormControl>
              <Autocomplete
                className={'dropdown-autocomplete'}
                id="firmware-details-status"
                sx={{ width: '100%' }}
                value={values.status}
                onBlur={handleBlur}
                isOptionEqualToValue={optionEquality}
                onChange={setStatusValue}
                renderInput={statusRenderInput}
                options={optionsStatus.map(
                  mapActiveInactiveStatusEnumToItemLabelModel
                )}
                getOptionLabel={o => convertToSentenceCase(o.label)}
              />
            </FormControl>
            <FormControl
              classes={{
                root: 'button-container',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  classes={{
                    root: 'button',
                  }}
                  className="btn btn-white"
                  onClick={cancelAction}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  classes={{
                    root: 'button',
                  }}
                  className="btn btn-primary"
                >
                  Save
                </Button>
              </Box>
            </FormControl>
          </Box>
        </Box>
      </ThemeProvider>
      <Modal>{ctx.component}</Modal>
    </>
  );
};

export default FirmwareDetailsForm;
